/**
 * Phase 30 — Milestone CRUD API client.
 *
 * Endpoints (per Plan 30-02 / D-14):
 *   GET    /api/projects/:projectId/milestones  (member-readable, incl. external)
 *   POST   /api/projects/:projectId/milestones  (admin or PM)
 *   PATCH  /api/milestones/:id                  (admin or PM)
 *   DELETE /api/milestones/:id                  (admin or PM)
 *
 * Date fields are YYYY-MM-DD strings (server converts from Postgres DATE per D-04).
 * Timestamps are ISO 8601 strings.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export interface Milestone {
  id: number;
  projectId: number;
  name: string;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null; // YYYY-MM-DD
  // 260913-l8n / MKS-174 — server-derived read-time status; NOT directly
  // writable (see CreateMilestoneInput / UpdateMilestoneInput below, which
  // stay two-valued as the write contract).
  status: 'active' | 'completed' | 'planned';
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

export interface CreateMilestoneInput {
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  status?: 'active' | 'completed';
}

export interface UpdateMilestoneInput {
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  status?: 'active' | 'completed';
}

export async function fetchMilestones(projectId: string): Promise<Milestone[]> {
  const res = await apiGet<{ data: Milestone[] }>(`/projects/${projectId}/milestones`);
  return res.data;
}

export async function createMilestone(
  projectId: string,
  input: CreateMilestoneInput,
): Promise<Milestone> {
  const res = await apiPost<{ data: Milestone }>(
    `/projects/${projectId}/milestones`,
    input,
  );
  return res.data;
}

export async function updateMilestone(
  id: number,
  input: UpdateMilestoneInput,
): Promise<Milestone> {
  const res = await apiPatch<{ data: Milestone }>(`/milestones/${id}`, input);
  return res.data;
}

export async function deleteMilestone(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/milestones/${id}`);
}
